import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [guideDropdownOpen, setGuideDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const guideDropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (guideDropdownRef.current && !guideDropdownRef.current.contains(e.target as Node)) {
        setGuideDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
  }, [location]);

  const guideArticles = [
    { slug: 'cssbuy-spreadsheet-guia-completo-2026', label: 'Guia Definitivo 2026' },
    { slug: 'cssbuy-spreadsheet-tutorial-iniciantes', label: 'Tutorial para Iniciantes' },
    { slug: 'cssbuy-spreadsheet-e-seguro', label: 'Segurança e Confiabilidade' },
    { slug: 'economizar-dinheiro-cssbuy-spreadsheet', label: 'Dicas de Economia' },
    { slug: 'shipping-barato-china-cssbuy', label: 'Shipping Barato' },
    { slug: 'qual-melhor-agent-china-2026-review', label: 'Melhor Agente 2026' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-black/35 backdrop-blur-sm'
      }`}
    >
      <div className="px-4 md:px-6 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">

          <div className="flex items-center gap-3 md:gap-6">
            <Link
              to="/"
              className={`font-heading text-lg md:text-xl font-bold tracking-wide whitespace-nowrap transition-colors duration-300 ${
                scrolled ? 'text-foreground-950' : 'text-white'
              }`}
            >
              CSSBuy Spreadsheet
            </Link>

            <div className="hidden md:flex items-center gap-2">
              <Link
                to="/"
                className={`text-sm font-medium whitespace-nowrap px-3 py-2 rounded-full transition-colors cursor-pointer ${
                  scrolled
                    ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-200/70'
                    : 'text-white/90 hover:text-white hover:bg-white/15'
                }`}
              >
                Início
              </Link>

              <div ref={guideDropdownRef} className="relative">
                <button
                  onClick={() => setGuideDropdownOpen(!guideDropdownOpen)}
                  className={`flex items-center gap-1.5 text-sm font-medium whitespace-nowrap px-3 py-2 rounded-full transition-colors cursor-pointer ${
                    scrolled
                      ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-200/70'
                      : 'text-white/90 hover:text-white hover:bg-white/15'
                  }`}
                >
                  Guia de Compras
                  <i className={`text-xs w-3 h-3 flex items-center justify-center ${guideDropdownOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
                </button>

                {guideDropdownOpen && (
                  <div className="absolute left-0 mt-2 w-64 bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden z-30">
                    {guideArticles.map((article) => (
                      <Link
                        key={article.slug}
                        to={`/artigo/${article.slug}`}
                        className="block px-5 py-3 text-sm text-foreground-800 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                        onClick={() => setGuideDropdownOpen(false)}
                      >
                        {article.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 md:gap-4">

            <div ref={dropdownRef} className="relative hidden md:block">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  scrolled
                    ? 'bg-foreground-950 text-background-50 hover:bg-foreground-800'
                    : 'bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 border border-white/30'
                }`}
              >
                <span>Categorias</span>
                <i className={`text-base w-4 h-4 flex items-center justify-center ${dropdownOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-72 bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden max-h-96 overflow-y-auto z-30">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <div className="w-9 h-9 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                        <img src={cat.thumbUrl} alt={cat.namePt} className="w-full h-full object-cover" />
                      </div>
                      <span className="flex-1 whitespace-nowrap">{cat.namePt}</span>
                      <span className="text-foreground-400 text-xs">({cat.name})</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer bg-primary-500 text-background-50 hover:bg-primary-600"
            >
              Buy It Now
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
            </a>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`md:hidden w-10 h-10 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                scrolled
                  ? 'text-foreground-950 hover:bg-background-200'
                  : 'text-white hover:bg-white/15'
              }`}
              aria-label="Menu"
            >
              <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200/70 mt-0 -mx-4 px-4 py-4 space-y-3 max-h-[70vh] overflow-y-auto">
            <Link
              to="/"
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-home-4-line text-lg w-5 h-5 flex items-center justify-center text-primary-500"></i>
              Início
            </Link>

            <div className="px-4 pt-2">
              <p className="text-xs font-semibold text-foreground-500 uppercase tracking-widest mb-2">Guia de Compras</p>
              <div className="space-y-1">
                {guideArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/artigo/${article.slug}`}
                    className="block px-4 py-2.5 rounded-xl text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    {article.label}
                  </Link>
                ))}
              </div>
            </div>

            <div className="px-4 pt-2">
              <p className="text-xs font-semibold text-foreground-500 uppercase tracking-widest mb-2">Categorias</p>
              <div className="space-y-1">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                      <img src={cat.thumbUrl} alt={cat.namePt} className="w-full h-full object-cover" />
                    </div>
                    <span>{cat.namePt}</span>
                  </a>
                ))}
              </div>
            </div>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-shopping-cart-2-line text-lg w-5 h-5 flex items-center justify-center"></i>
              Buy It Now
            </a>
          </div>
        )}
      </div>
    </nav>
  );
}